import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import type { ServiceDetail, TelemetryEvent } from "@/lib/telemetry";
import { formatTs, type TelemetryTz } from "@/components/telemetry/format";

/**
 * What each marker means, in the words a support engineer needs.
 *
 * Descriptive, never diagnostic: a rejected frame is a frame whose checksum did
 * not validate. Whether that was radio interference, a truncating NAT or a
 * failing unit is not something the archive knows.
 */
const MARKER: Record<string, { label: string; note: string; variant: "neutral" | "warning" }> = {
  DBG: { label: "DBG", note: "debug frame — no telemetry", variant: "neutral" },
  EKM: { label: "EKM", note: "meter id frame — no telemetry", variant: "neutral" },
  GPS: { label: "GPS", note: "location frame — no telemetry", variant: "neutral" },
  REJECTED: { label: "Rejected", note: "checksum did not validate — not processed", variant: "warning" },
};

export function serviceMarkerOf(event: TelemetryEvent): string {
  const detail = event.detail as ServiceDetail | null;
  return (detail?.marker || event.state || "—").toString();
}

/**
 * The frames that are not telemetry, kept apart from the ones that are.
 *
 * They used to sit in the events table as `charging → DBG` transitions and as
 * anomalies raised on their placeholder zeros. Here they are what they always
 * were: the charger talking to the server about itself. Readings are not shown
 * because these frames carry none — the raw frame, for an admin, is a click
 * away in the drawer.
 */
export function ServiceFramesTable({
  events,
  tz,
  isLoading,
  onSelect,
}: {
  events: TelemetryEvent[];
  tz: TelemetryTz;
  isLoading?: boolean;
  onSelect?: (event: TelemetryEvent) => void;
}) {
  const columns = React.useMemo<ColumnDef<TelemetryEvent, any>[]>(
    () => [
      {
        id: "ts",
        header: tz === "utc" ? "Time (UTC)" : "Time",
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">{formatTs(row.original.ts, tz)}</span>
        ),
      },
      {
        id: "frame",
        header: "Frame",
        cell: ({ row }) => {
          const marker = serviceMarkerOf(row.original);
          const spec = MARKER[marker];
          return <Badge variant={spec?.variant ?? "neutral"}>{spec?.label ?? marker}</Badge>;
        },
      },
      {
        id: "note",
        header: "What it is",
        cell: ({ row }) => {
          const marker = serviceMarkerOf(row.original);
          const detail = row.original.detail as ServiceDetail | null;
          const note = MARKER[marker]?.note ?? "not telemetry";
          const state = row.original.state;
          const extra =
            detail?.marker === "REJECTED" && state ? ` · parsed as ${state}` : "";
          return <span className="text-xs text-muted-foreground">{note + extra}</span>;
        },
      },
    ],
    [tz],
  );

  return (
    <DataTableShell height="auto" minHeight="0" className="[&>div]:max-h-[320px]">
      <DataTable
        columns={columns}
        data={events}
        isLoading={isLoading}
        onRowClick={onSelect}
        tableClassName="text-xs"
        emptyMessage="No service frames in this window."
      />
    </DataTableShell>
  );
}
